/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { ICON_LOCK, ICON_LOCK_OPEN, TEXTFIELD_TYPE_TEXTAREA, TextField } from '@squiz/sds';
import isUndefined from 'lodash/isUndefined';
import property from 'lodash/property';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CDP_STRINGS } from '../../../../Constants/Strings.constants';
import { SEGMENT_BUILDER_DATA_IDS, SEGMENT_BUILDER_FORM_SELECT_IDS } from '../../SegmentBuilder.constants';
import { SegmentDetailsProps } from './SegmentDetails.props';

/**
 * The details panel in the segment builder.
 * @param {SegmentDetailsProps} props Props passed to `<SegmentDetails />`.
 * @returns {JSX.Element} The `<SegmentDetails />` component.
 */
export const SegmentDetails = (props: SegmentDetailsProps): JSX.Element => {
  const { t } = useTranslation();
  const { description, errors, handleCreateDevId, handleInputChange, id, internalId, name, sectionName } = props;
  const isCreate = useMemo((): boolean => {
    return isUndefined(internalId) || internalId.startsWith(`tmp-`);
  }, [internalId]);
  const [isLocked, setToLocked] = useState(false);

  // Labels
  const labels = useMemo(() => {
    return {
      descriptionLabel: t(CDP_STRINGS.descriptionFieldLabel),
      idLabel: t(CDP_STRINGS.idFieldLabel),
      idNote: t(CDP_STRINGS.idFieldNote),
      nameLabel: t(CDP_STRINGS.nameFieldLabel),
      nameNote: t(CDP_STRINGS.nameFieldNote),
    };
  }, [t]);

  useEffect(() => {
    setToLocked(isCreate === false);
  }, [isCreate]);

  return (
    <div data-id={SEGMENT_BUILDER_DATA_IDS.segmentDetailsSection}>
      <h3 className="sds-row__title details-section-title">{sectionName}</h3>
      <div className="sds-grid details-section">
        <div className="sds-grid__item">
          <TextField
            data-id={SEGMENT_BUILDER_DATA_IDS.segmentDetailsName}
            errorMessage={
              property(`${SEGMENT_BUILDER_FORM_SELECT_IDS.name}-${internalId}`)(errors) as string | undefined
            }
            fieldLabel={labels.nameLabel}
            id={`${SEGMENT_BUILDER_FORM_SELECT_IDS.name}-${internalId}`}
            name={`${SEGMENT_BUILDER_FORM_SELECT_IDS.name}-${internalId}`}
            note={labels.nameNote}
            onBlur={handleCreateDevId}
            onChange={handleInputChange}
            required
            value={name}
          />
          <TextField
            classes={`details-section__code`}
            disabled={isLocked}
            errorMessage={property(`${SEGMENT_BUILDER_FORM_SELECT_IDS.id}-${internalId}`)(errors) as string | undefined}
            fieldLabel={labels.idLabel}
            icon={isLocked ? ICON_LOCK : ICON_LOCK_OPEN}
            id={`${SEGMENT_BUILDER_FORM_SELECT_IDS.id}-${internalId}`}
            maxCharacters={50}
            name={`${SEGMENT_BUILDER_FORM_SELECT_IDS.id}-${internalId}`}
            note={labels.idNote}
            onChange={handleInputChange}
            required={!isLocked}
            validationPattern="^[a-z0-9]+(?:-[a-z0-9]+)*$"
            value={id}
          />
        </div>
        <div className="sds-grid__item">
          <TextField
            data-id={SEGMENT_BUILDER_DATA_IDS.segmentDetailsDescription}
            fieldLabel={labels.descriptionLabel}
            id={`${SEGMENT_BUILDER_FORM_SELECT_IDS.description}-${internalId}`}
            name={`${SEGMENT_BUILDER_FORM_SELECT_IDS.description}-${internalId}`}
            onChange={handleInputChange}
            rows={8}
            type={TEXTFIELD_TYPE_TEXTAREA}
            value={description}
          />
        </div>
      </div>
    </div>
  );
};
